import { useState, useCallback, useMemo, useEffect, useRef } from 'react';

import CandidatesService from '../../../services/CandidatesService';
import IntelSearchStorageService from '../../../services/IntelSearchStorageService';

// Default filter values for comparison
const DEFAULT_FILTERS = {
  yearsExperience: [0, 5],
  seniority: null,
  country: [],
  city: '',
  workMode: [],
  willingToRelocate: false,
  immediateJoiner: false,
  noticePeriod: null,
  shift: null,
  shiftReadiness: null,
  timezoneOverlap: null,
  travelPercentage: null,
  compensationType: 'Expected',
  currency: null,
  frequency: null,
  salaryRange: [0, 100000],
  salaryIncrease: [0, 100],
  visaWorkPermit: [],
  languages: [],
  proficiencyLevel: null,
  employmentStability: true,
  engagementPreference: null,
  willingnessToUpskill: null, // null = Any (default), true = Yes, false = No
  jobAppliedFilter: null, // null = Any (default), { value: 0, label: 'None' } or { value: jobId, label: jobTitle }
};

/**
 * Custom hook for Intel Search state management and business logic
 * Centralizes search state, filter state, and API payload building
 * @param {number|null} currentUserId - Current user ID for tracking recent searches (optional)
 * @param {Function} onRecentSearchAdded - Callback called when a search is added to recent (optional)
 */
export default function useIntelSearch(
  currentUserId = null,
  onRecentSearchAdded = null
) {
  // Refs for managing async operations
  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isLoadingSearchRef = useRef(false); // Flag to prevent auto-check during load
  const activeJobTimeoutRef = useRef(null); // Track timeout set by activeJob effect
  const shouldAddToRecentRef = useRef(false); // Flag to track if search should be added to recent
  const searchPayloadForRecentRef = useRef(null); // Store search payload to add to recent

  // Track sections that were manually unchecked by the user
  // This prevents auto-checking sections that the user explicitly disabled
  const manuallyUncheckedSectionsRef = useRef(new Set());

  // Track sections that were auto-checked (by setting a filter value)
  // These should auto-uncheck when all values return to defaults
  const autoCheckedSectionsRef = useRef(new Set());

  // UI State
  const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(true);

  // Search State
  const [searchInput, setSearchInput] = useState('');
  const [searchChips, setSearchChips] = useState([]);

  // Filter State
  const [filters, setFilters] = useState({
    // Experience & Role
    yearsExperience: [0, 5],
    seniority: null,

    // Location & Work
    country: [], // Multi-select
    city: '',
    workMode: [],
    willingToRelocate: false,

    // Availability
    immediateJoiner: false,
    noticePeriod: null,
    shift: null,
    shiftReadiness: null,
    timezoneOverlap: null,
    travelPercentage: null,

    // Compensation
    compensationType: 'Expected', // 'Expected' or 'Current'
    currency: null,
    frequency: null,
    salaryRange: [0, 100000],
    salaryIncrease: [0, 100],

    // Work Authorization
    visaWorkPermit: [], // Multi-select

    // Languages & Communication
    languages: [], // Array of {language: {label, value}, level: {label, value}}

    // Stability & Growth
    employmentStability: true,
    engagementPreference: null,
    willingnessToUpskill: null, // null = Any (default), true = Yes, false = No

    // Jobs Applied
    jobAppliedFilter: null, // null = Any, { value: 0, label: 'None' } or { value: jobId, label: jobTitle }
  });

  // Results State
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalResults: 0,
    pageSize: 20,
    totalPages: 1,
  });
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [sortBy, setSortBy] = useState('totalMatchCount:desc'); // Default: Best keyword match first

  // Track if search has been run at least once (for auto-search on filter changes)
  const [hasSearched, setHasSearched] = useState(false);

  // Save Search Modal State
  const [showSaveSearchModal, setShowSaveSearchModal] = useState(false);

  // Track active saved search and its original state
  const [activeSavedSearch, setActiveSavedSearch] = useState(null);
  const [originalSearchState, setOriginalSearchState] = useState(null);

  // Track active job from URL parameters
  const [activeJob, setActiveJob] = useState(null); // { id: number, title: string }

  // Track which sections are active/enabled (only active sections go into payload)
  const [activeSections, setActiveSections] = useState({
    experienceRole: false,
    locationWork: false,
    availability: false,
    compensation: false,
    workAuth: false,
    languages: false,
    stability: false,
    jobsApplied: false,
  });

  // Helper to check if values are equal (handles arrays and objects)
  const areValuesEqual = (val1, val2) => {
    if (Array.isArray(val1) && Array.isArray(val2)) {
      if (val1.length !== val2.length) return false;
      return val1.every((v, i) => v === val2[i]);
    }
    return val1 === val2;
  };

  // Check if a section has non-default values
  const sectionHasNonDefaultValues = useMemo(() => {
    const hasNonDefault = {
      experienceRole:
        !areValuesEqual(
          filters.yearsExperience,
          DEFAULT_FILTERS.yearsExperience
        ) || filters.seniority !== DEFAULT_FILTERS.seniority,

      locationWork:
        filters.country?.length > 0 ||
        filters.city !== DEFAULT_FILTERS.city ||
        filters.workMode?.length > 0 ||
        filters.willingToRelocate !== DEFAULT_FILTERS.willingToRelocate,

      availability:
        filters.immediateJoiner !== DEFAULT_FILTERS.immediateJoiner ||
        filters.noticePeriod !== DEFAULT_FILTERS.noticePeriod ||
        filters.shift !== DEFAULT_FILTERS.shift ||
        filters.shiftReadiness !== DEFAULT_FILTERS.shiftReadiness ||
        filters.timezoneOverlap !== DEFAULT_FILTERS.timezoneOverlap ||
        filters.travelPercentage !== DEFAULT_FILTERS.travelPercentage,

      compensation:
        filters.compensationType !== DEFAULT_FILTERS.compensationType ||
        filters.currency !== DEFAULT_FILTERS.currency ||
        filters.frequency !== DEFAULT_FILTERS.frequency ||
        !areValuesEqual(filters.salaryRange, DEFAULT_FILTERS.salaryRange) ||
        !areValuesEqual(filters.salaryIncrease, DEFAULT_FILTERS.salaryIncrease),

      workAuth: filters.visaWorkPermit?.length > 0,

      languages:
        filters.languages?.length > 0 &&
        filters.languages.some((lang) => lang.language !== null),

      stability:
        filters.employmentStability !== DEFAULT_FILTERS.employmentStability ||
        filters.engagementPreference !== DEFAULT_FILTERS.engagementPreference ||
        filters.willingnessToUpskill !== DEFAULT_FILTERS.willingnessToUpskill,

      jobsApplied:
        filters.jobAppliedFilter !== null &&
        filters.jobAppliedFilter !== undefined,
    };

    return hasNonDefault;
  }, [filters]);

  // Auto-check sections when filter values have non-default values (user sets filters)
  // Auto-uncheck sections that were auto-checked when values return to defaults
  // Manually checked sections (via the checkbox) stay checked regardless of values
  useEffect(() => {
    // Skip auto-update if we're currently loading a saved search
    if (isLoadingSearchRef.current) {
      return;
    }

    setActiveSections((prev) => {
      const updated = { ...prev };

      Object.keys(sectionHasNonDefaultValues).forEach((section) => {
        const currentHasNonDefault = sectionHasNonDefaultValues[section];
        const wasChecked = prev[section] === true;

        if (currentHasNonDefault) {
          if (!wasChecked) {
            updated[section] = true;
            autoCheckedSectionsRef.current.add(section);
            manuallyUncheckedSectionsRef.current.delete(section);
          }
        } else if (wasChecked && autoCheckedSectionsRef.current.has(section)) {
          // Values returned to defaults and section was auto-checked → auto-uncheck
          updated[section] = false;
          autoCheckedSectionsRef.current.delete(section);
        }
      });

      return updated;
    });
  }, [sectionHasNonDefaultValues]);

  // Map each section to its filter keys for resetting on uncheck
  const sectionFilterKeys = useMemo(
    () => ({
      experienceRole: ['yearsExperience', 'seniority'],
      locationWork: ['country', 'city', 'workMode', 'willingToRelocate'],
      availability: [
        'immediateJoiner',
        'noticePeriod',
        'shift',
        'shiftReadiness',
        'timezoneOverlap',
        'travelPercentage',
      ],
      compensation: [
        'compensationType',
        'currency',
        'frequency',
        'salaryRange',
        'salaryIncrease',
      ],
      workAuth: ['visaWorkPermit'],
      languages: ['languages'],
      stability: [
        'employmentStability',
        'engagementPreference',
        'willingnessToUpskill',
      ],
      jobsApplied: ['jobAppliedFilter'],
    }),
    []
  );

  // Section Actions
  const toggleSection = useCallback(
    (section) => {
      const isBeingUnchecked = activeSections[section] === true;

      setActiveSections((prev) => {
        const newValue = !prev[section];

        if (!newValue) {
          manuallyUncheckedSectionsRef.current.add(section);
        } else {
          manuallyUncheckedSectionsRef.current.delete(section);
        }
        autoCheckedSectionsRef.current.delete(section);

        return {
          ...prev,
          [section]: newValue,
        };
      });

      // Reset filters to defaults when unchecking a section
      if (isBeingUnchecked) {
        const keys = sectionFilterKeys[section];
        if (keys) {
          setFilters((prev) => {
            const resetValues = {};
            keys.forEach((key) => {
              if (DEFAULT_FILTERS[key] !== undefined) {
                resetValues[key] = Array.isArray(DEFAULT_FILTERS[key])
                  ? [...DEFAULT_FILTERS[key]]
                  : DEFAULT_FILTERS[key];
              }
            });
            return { ...prev, ...resetValues };
          });
        }
      }
    },
    [activeSections, sectionFilterKeys]
  );

  // Search Actions
  const addSearchChip = useCallback(
    (chip) => {
      if (chip && !searchChips.includes(chip)) {
        setSearchChips((prev) => [...prev, chip]);
      }
    },
    [searchChips]
  );

  const removeSearchChip = useCallback((chipToRemove) => {
    setSearchChips((prev) => prev.filter((chip) => chip !== chipToRemove));
  }, []);

  const clearSearchChips = useCallback(() => {
    setSearchChips([]);
  }, []);

  // Filter Actions
  const updateFilter = useCallback((filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  }, []);

  const updateFilters = useCallback((updates) => {
    setFilters((prev) => ({
      ...prev,
      ...updates,
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({
      yearsExperience: [0, 5],
      seniority: null,
      country: [],
      city: '',
      workMode: [],
      willingToRelocate: false,
      immediateJoiner: false,
      noticePeriod: null,
      shift: null,
      shiftReadiness: null,
      timezoneOverlap: null,
      travelPercentage: null,
      compensationType: 'Expected',
      currency: null,
      frequency: null,
      salaryRange: [0, 100000],
      salaryIncrease: [0, 100],
      visaWorkPermit: [],
      languages: [],
      employmentStability: true,
      engagementPreference: null,
      willingnessToUpskill: null,
      jobAppliedFilter: null,
    });
    setActiveSections({
      experienceRole: false,
      locationWork: false,
      availability: false,
      compensation: false,
      workAuth: false,
      languages: false,
      stability: false,
      jobsApplied: false,
    });
    manuallyUncheckedSectionsRef.current.clear();
    autoCheckedSectionsRef.current.clear();
  }, []);

  // Selection Actions (Exclusive - only one candidate can be selected at a time)
  const toggleCandidateSelection = useCallback((candidateId) => {
    setSelectedCandidates((prev) =>
      prev.includes(candidateId) ? [] : [candidateId]
    );
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedCandidates([]);
  }, []);

  // Note: selectAll is kept for API consistency but only selects the first candidate
  const selectAll = useCallback(() => {
    if (results.length > 0) {
      setSelectedCandidates([results[0].id]);
    }
  }, [results]);

  // UI Actions
  const toggleLeftPanel = useCallback(() => {
    setIsLeftPanelCollapsed((prev) => !prev);
  }, []);

  // Payload Builder
  const buildSearchPayload = useCallback(() => {
    // Helper to extract ID(s) from dropdown selections
    const extractIds = (value) => {
      if (!value) return null;
      if (Array.isArray(value)) {
        const ids = value.map((item) => item?.value).filter(Boolean);
        return ids.length > 0 ? ids : null;
      }
      return value?.value || null;
    };

    // Separate skill IDs, certification names, and keywords from search chips
    const skillIds = [];
    const certificationNames = [];
    const keywords = [];

    searchChips.forEach((chip) => {
      if (typeof chip === 'string') {
        // Legacy string chip - treat as keyword
        keywords.push(chip);
      } else if (chip.type === 'skill' && chip.id) {
        // Skill chip with ID - exact match by ID
        skillIds.push(chip.id);
      } else if (chip.type === 'certification' && chip.value) {
        // Certification chip - exact match by name (case-insensitive)
        certificationNames.push(chip.value);
      } else if (chip.type === 'keyword' && chip.value) {
        // Keyword chip - contains match
        keywords.push(chip.value);
      }
    });

    const payload = {
      // Pagination
      pageNumber: pagination.currentPage,
      pageSize: pagination.pageSize,
      // Sorting
      orderBy: sortBy,
    };

    // Add skill IDs if any (exact match by ID)
    if (skillIds.length > 0) {
      payload.skillIds = skillIds;
    }

    // Add job ID if active
    if (activeJob?.id) {
      payload.jobId = activeJob.id;
    }

    // Add certification names if any (exact match by name)
    if (certificationNames.length > 0) {
      payload.certificationNames = certificationNames;
    }

    // Add keywords if any (contains match)
    if (keywords.length > 0) {
      payload.keywords = keywords;
    }

    // Only include filters from active sections
    // Experience & Role: always send experience range when section is active
    // (default [0,5] is a valid filter criterion, same pattern as stability booleans)
    if (activeSections.experienceRole) {
      const [minExp, maxExp] = filters.yearsExperience;
      payload.yearsExperienceMin = minExp;
      payload.yearsExperienceMax = maxExp;

      if (filters.seniority !== DEFAULT_FILTERS.seniority) {
        payload.seniorityId = extractIds(filters.seniority);
      }
    }

    if (
      activeSections.locationWork &&
      sectionHasNonDefaultValues.locationWork
    ) {
      if (filters.country?.length > 0) {
        payload.countryCodes = extractIds(filters.country);
      }
      if (filters.city !== DEFAULT_FILTERS.city) {
        payload.city = filters.city;
      }
      if (filters.workMode?.length > 0) {
        payload.workModeIds = extractIds(filters.workMode);
      }
      if (filters.willingToRelocate !== DEFAULT_FILTERS.willingToRelocate) {
        payload.willingToRelocate = filters.willingToRelocate;
      }
    }

    if (
      activeSections.availability &&
      sectionHasNonDefaultValues.availability
    ) {
      if (filters.immediateJoiner !== DEFAULT_FILTERS.immediateJoiner) {
        payload.immediateJoiner = filters.immediateJoiner;
      }
      if (filters.noticePeriod !== DEFAULT_FILTERS.noticePeriod) {
        payload.noticePeriodId = extractIds(filters.noticePeriod);
      }
      if (filters.shift !== DEFAULT_FILTERS.shift) {
        payload.workShiftId = extractIds(filters.shift);
      }
      if (filters.shiftReadiness !== DEFAULT_FILTERS.shiftReadiness) {
        payload.workShiftReadinessId = extractIds(filters.shiftReadiness);
      }
      if (filters.timezoneOverlap !== DEFAULT_FILTERS.timezoneOverlap) {
        payload.timezoneOverlap = extractIds(filters.timezoneOverlap);
      }
      if (filters.travelPercentage !== DEFAULT_FILTERS.travelPercentage) {
        payload.travelPercentage = extractIds(filters.travelPercentage);
      }
    }

    if (
      activeSections.compensation &&
      sectionHasNonDefaultValues.compensation
    ) {
      if (filters.compensationType !== DEFAULT_FILTERS.compensationType) {
        payload.compensationType = filters.compensationType;
      }
      if (filters.currency !== DEFAULT_FILTERS.currency) {
        payload.currencyId = extractIds(filters.currency);
      }
      if (filters.frequency !== DEFAULT_FILTERS.frequency) {
        payload.salaryFrequencyId = extractIds(filters.frequency);
      }
      if (!areValuesEqual(filters.salaryRange, DEFAULT_FILTERS.salaryRange)) {
        const [minSalary, maxSalary] = filters.salaryRange;
        payload.salaryMin = minSalary;
        payload.salaryMax = maxSalary;
      }
      if (
        !areValuesEqual(filters.salaryIncrease, DEFAULT_FILTERS.salaryIncrease)
      ) {
        const [minIncrease, maxIncrease] = filters.salaryIncrease;
        payload.salaryIncreaseMin = minIncrease;
        payload.salaryIncreaseMax = maxIncrease;
      }
    }

    if (activeSections.workAuth && sectionHasNonDefaultValues.workAuth) {
      if (filters.visaWorkPermit?.length > 0) {
        payload.visaWorkPermitIds = extractIds(filters.visaWorkPermit);
      }
    }

    if (activeSections.languages && sectionHasNonDefaultValues.languages) {
      if (filters.languages?.length > 0) {
        // Filter: only include entries that have at least a language selected
        // Level is optional per entry
        const languageSelections = filters.languages
          .filter((lang) => lang.language) // Only language is required
          .map((lang) => ({
            languageId: lang.language.value,
            ...(lang.level && { languageLevelId: lang.level.value }), // Include level only if selected
          }));

        if (languageSelections.length > 0) {
          payload.languageSelections = languageSelections;
          // eslint-disable-next-line no-console
          console.log(
            '✅ Language selections added to payload:',
            languageSelections
          );
        } else {
          // eslint-disable-next-line no-console
          console.log('⚠️ Languages exist but none have a language selected');
        }
      }
    } else {
      // eslint-disable-next-line no-console
      console.log(
        '❌ Languages not in payload. Active:',
        activeSections.languages,
        'HasNonDefault:',
        sectionHasNonDefaultValues.languages
      );
    }

    // Stability section: when active, always send boolean values (they are meaningful filters)
    // Unlike other sections, the default values (true/true) are valid filter criteria
    if (activeSections.stability) {
      // Always send boolean values when section is active
      payload.employmentStability = filters.employmentStability;

      // Only send willingness to upskill if user picked Yes/No (null = Any → omit)
      if (filters.willingnessToUpskill !== null) {
        payload.willingnessToUpskill = filters.willingnessToUpskill;
      }

      // Only send engagement preference if selected
      if (filters.engagementPreference) {
        payload.engagementPreferenceId = extractIds(
          filters.engagementPreference
        );
      }
    }

    // Jobs Applied filter (section-gated like other sections)
    if (
      activeSections.jobsApplied &&
      filters.jobAppliedFilter !== null &&
      filters.jobAppliedFilter !== undefined
    ) {
      payload.jobAppliedFilter = filters.jobAppliedFilter.value;
    }

    // Remove null/undefined values for cleaner payload
    return Object.fromEntries(
      Object.entries(payload).filter(([, value]) => {
        if (value === null || value === undefined) return false;
        if (Array.isArray(value) && value.length === 0) return false;
        return true;
      })
    );
  }, [
    searchChips,
    filters,
    pagination,
    sortBy,
    activeSections,
    sectionHasNonDefaultValues,
    activeJob,
  ]);

  // Map backend response to CandidateCard format
  const mapCandidateResponse = useCallback((candidate) => {
    const profile = candidate.userProfile || {};
    const careerInfo = candidate.careerInfo || {};

    // Get current job title and company from career experiences
    const currentExperience = careerInfo.careerExperiences?.[0];

    // Calculate years of experience (raw double, matching backend logic)
    const calculateYearsOfExperience = () => {
      if (!careerInfo.careerExperiences?.length) return 0;

      let totalYears = 0;
      careerInfo.careerExperiences.forEach((exp) => {
        if (exp.startDate) {
          const start = new Date(exp.startDate);
          const end = exp.endDate ? new Date(exp.endDate) : new Date();
          const years = (end - start) / (1000 * 60 * 60 * 24 * 365.25);
          totalYears += years;
        }
      });

      return totalYears;
    };

    const rawYearsExp = calculateYearsOfExperience();
    const yearsExp = Math.round(rawYearsExp);
    const skillsCount = careerInfo.skills?.length || 0;

    // Seniority boundaries must match the backend (CandidateService.cs):
    // < 2 → Junior, < 5 → Mid, < 10 → Senior, >= 10 → Lead
    const getSeniorityLevel = (years) => {
      if (years < 2) return 'Junior';
      if (years < 5) return 'Mid';
      if (years < 10) return 'Senior';
      return 'Lead';
    };

    // Build metrics array for display
    const metrics = [];
    if (yearsExp > 0) {
      const seniority = getSeniorityLevel(rawYearsExp);
      metrics.push(`${yearsExp}+ years exp (${seniority})`);
    }
    if (skillsCount > 0) metrics.push(`${skillsCount} skills`);
    if (careerInfo.careerEducations?.length) {
      metrics.push(`${careerInfo.careerEducations.length} degrees`);
    }
    if (careerInfo.careerExperiences?.length) {
      metrics.push(
        `${careerInfo.careerExperiences.length} ${
          careerInfo.careerExperiences.length === 1 ? 'employer' : 'employers'
        }`
      );
    }

    // Highlight special status
    if (careerInfo.immediateJoiner === true) {
      metrics.push('Immediate Joiner');
    }
    if (careerInfo.isFresher === true || profile.isFresher === true) {
      metrics.push('Fresher');
    }

    // Build match sections with keyword match counts if available
    const matchSections = {
      resume: candidate.totalCareerInfoMatchCount || 0,
      note: candidate.noteMatchCount || 0,
      personal: candidate.personalMatchCount || 0,
    };

    return {
      id: candidate.id,
      firstName: profile.firstName || '',
      lastName: profile.lastName || '',
      avatar: profile.avatar || null,
      jobTitle:
        currentExperience?.jobTitle || careerInfo.currentJobTitle || 'N/A',
      company: currentExperience?.empoyerName || 'N/A',
      phone: profile.phone || 'N/A',
      source: candidate.sourceName || 'Platform',
      submissionsCount: candidate.submissions || 0,
      submissionsJobApplications:
        candidate.submissionsJobApplications?.map((app) => ({
          jobId: app.jobPost?.id || 0,
          jobTitle: app.jobPost?.jobTitle || 'N/A',
          companyName: app.jobPost?.company?.name || 'N/A',
        })) || [],
      isFavorite: candidate.isFavorite || false,
      lastViewed: candidate.viewedAt || null,
      lastActivity: candidate.lastLoginAt || null,
      matchSections,
      metrics, // Array of metric strings
      matchPercentage: candidate.jobMatchPercent || null,
      // Keep original data for detail views
      _original: candidate,
    };
  }, []);

  // Check if there are any search criteria (keywords or active filters)
  const hasSearchCriteria = useCallback(() => {
    // Check if there are any search chips
    if (searchChips.length > 0) return true;

    // Check if any section is active (which means it has non-default values)
    // This now includes jobsApplied section
    return Object.values(activeSections).some((isActive) => isActive);
  }, [searchChips, activeSections]);

  // API Integration Point - Internal search function (not debounced)
  const executeSearch = useCallback(
    async (providedPayload = null) => {
      // Don't search if there are no criteria AND no activeJob (unless a payload is explicitly provided)
      // jobId is treated the same as search criteria - it should trigger a search
      if (!providedPayload && !hasSearchCriteria() && !activeJob) {
        // Clear results and show empty state
        setResults([]);
        setPagination({
          currentPage: 1,
          totalResults: 0,
          pageSize: 20,
          totalPages: 1,
        });
        setIsLoading(false);
        return;
      }

      // Cancel previous request if still in flight
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();

      setIsLoading(true);
      try {
        // Use provided payload if available, otherwise build from current state
        const payload = providedPayload || buildSearchPayload();

        // eslint-disable-next-line no-console
        console.log('🔍 Search triggered with payload:', payload);

        // Call the Intel Search API
        const response = await CandidatesService.intelSearch(
          payload,
          abortControllerRef.current.signal
        );

        // eslint-disable-next-line no-console
        console.log('📦 Backend response:', response);
        // eslint-disable-next-line no-console
        console.log('📦 Pagination header:', response.pagination);

        // Extract candidates array and pagination metadata from X-Pagination header
        const candidates = response.data || [];
        const paginationMeta = response.pagination || {};

        // eslint-disable-next-line no-console
        console.log('📦 Candidates array:', candidates);
        // eslint-disable-next-line no-console
        console.log('📦 Candidates count:', candidates.length);
        // eslint-disable-next-line no-console
        console.log('📦 Pagination metadata:', paginationMeta);

        // Map backend response to CandidateCard format
        const mappedResults = candidates.map(mapCandidateResponse);

        // eslint-disable-next-line no-console
        console.log('📦 Mapped results:', mappedResults);

        // Update results and pagination from X-Pagination header
        setResults(mappedResults);
        setPagination({
          currentPage: paginationMeta.CurrentPage || 1,
          totalResults: paginationMeta.TotalCount || 0,
          pageSize: paginationMeta.PageSize || 20,
          totalPages: paginationMeta.TotalPages || 1,
        });

        // Add to recent searches if flag is set and user ID is available
        if (shouldAddToRecentRef.current && currentUserId) {
          try {
            IntelSearchStorageService.init(currentUserId);
            // Use stored search payload if available (from loadSearch), otherwise use current state
            const searchState = searchPayloadForRecentRef.current || {
              searchChips,
              filters,
              activeSections,
              sortBy,
            };
            IntelSearchStorageService.addToRecent(searchState);
            shouldAddToRecentRef.current = false; // Reset flag
            searchPayloadForRecentRef.current = null; // Clear stored payload

            // Notify parent component to refresh left panel
            if (onRecentSearchAdded) {
              onRecentSearchAdded();
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error adding to recent searches:', error);
          }
        }
      } catch (error) {
        // Ignore abort errors (when user changes filters rapidly)
        if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
          // Reset flag on abort
          shouldAddToRecentRef.current = false;
          searchPayloadForRecentRef.current = null;
          return;
        }

        // eslint-disable-next-line no-console
        console.error('Intel Search error:', error);

        // Clear results on error
        setResults([]);
        setPagination((prev) => ({
          ...prev,
          totalResults: 0,
        }));

        // Reset flag on error
        shouldAddToRecentRef.current = false;
        searchPayloadForRecentRef.current = null;

        // TODO: Show error toast notification to user
      } finally {
        setIsLoading(false);
      }
    },
    [
      buildSearchPayload,
      mapCandidateResponse,
      hasSearchCriteria,
      activeJob,
      currentUserId,
      searchChips,
      filters,
      activeSections,
      sortBy,
      onRecentSearchAdded,
    ]
  );

  // Manual search function (exposed to components - called by "Run" button)
  const performSearch = useCallback(() => {
    // Mark that search has been run at least once (enables auto-search on filter changes)
    setHasSearched(true);

    // Set flag to add this search to recent history
    shouldAddToRecentRef.current = true;

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Reset to page 1 for new search criteria
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    // Override pageNumber in payload since setState is async
    const payload = buildSearchPayload();
    payload.pageNumber = 1;
    executeSearch(payload);
  }, [executeSearch, buildSearchPayload]);

  // Auto-search when filters or active sections change (NOT when keywords change)
  // Keywords should only trigger search via manual "Run" button
  useEffect(() => {
    // Skip auto-update if we're currently loading a saved search
    if (isLoadingSearchRef.current) {
      // Still return cleanup function to clear any pending timeouts
      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }

    const hasCriteria = hasSearchCriteria();

    // Scenario 1: There are search criteria -> trigger BE
    if (hasCriteria) {
      // Clear existing timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Set new timeout for debouncing
      searchTimeoutRef.current = setTimeout(() => {
        setHasSearched(true); // Mark that search has been performed
        // Reset to page 1 when search criteria change
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
        const payload = buildSearchPayload();
        payload.pageNumber = 1;
        executeSearch(payload);
      }, 300);
    }
    // Scenario 2: User removed all criteria -> reset to empty state
    else if (hasSearched && !hasCriteria) {
      setResults([]);
      setPagination({
        currentPage: 1,
        totalResults: 0,
        pageSize: 20,
        totalPages: 1,
      });
      setHasSearched(false);
    }

    // Cleanup function - always returned to ensure cleanup on unmount or dependency change
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, activeSections]);

  // Auto-search when activeJob changes (set or cleared)
  useEffect(() => {
    // Skip auto-update if we're currently loading a saved search
    if (isLoadingSearchRef.current) {
      // Still return cleanup function to clear any pending timeouts
      return () => {
        if (activeJobTimeoutRef.current) {
          clearTimeout(activeJobTimeoutRef.current);
        }
      };
    }

    const hasCriteria = hasSearchCriteria();
    const hasJob = activeJob !== null;

    // Update default sort when jobId changes
    // If jobId is present, use jobMatchPercent; otherwise use totalMatchCount
    const newSortBy = hasJob ? 'jobMatchPercent:desc' : 'totalMatchCount:desc';

    // Update sortBy state (this will trigger sortBy useEffect separately)
    // We handle search triggering in both places to ensure it works
    if (sortBy !== newSortBy) {
      setSortBy(newSortBy);
    }

    // Trigger search if there are search criteria (keywords or filters) OR if jobId is active
    // When jobId is set, it should trigger search even without other criteria
    // When jobId is cleared, trigger search if there are still other criteria
    if (hasCriteria || hasJob) {
      // Clear existing timeout from this effect
      if (activeJobTimeoutRef.current) {
        clearTimeout(activeJobTimeoutRef.current);
      }

      // Set new timeout for debouncing using a separate ref
      // This prevents sortBy useEffect from clearing it
      activeJobTimeoutRef.current = setTimeout(() => {
        setHasSearched(true); // Mark that search has been performed
        // Reset to page 1 when job context changes
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
        // Build payload explicitly - it will use the correct sortBy from state
        const payload = buildSearchPayload();
        // Override sortBy and pageNumber since setState is async
        payload.orderBy = newSortBy;
        payload.pageNumber = 1;
        executeSearch(payload);
        activeJobTimeoutRef.current = null;
      }, 400);
    }

    // Cleanup function
    return () => {
      if (activeJobTimeoutRef.current) {
        clearTimeout(activeJobTimeoutRef.current);
        activeJobTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeJob]);

  // Separate effect for sortBy and pagination changes (only trigger if already searched)
  useEffect(() => {
    // Skip auto-update if we're currently loading a saved search
    if (isLoadingSearchRef.current) {
      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }

    // Trigger search if:
    // 1. User has already searched (has results to re-sort), OR
    // 2. There's an activeJob (should trigger even on first sort change)
    const shouldTrigger = hasSearched || activeJob !== null;

    if (shouldTrigger) {
      // Clear existing timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // eslint-disable-next-line no-console
      console.log('🔄 Auto-search triggered (sort/pagination change)');

      // Set new timeout for debouncing
      // Use slightly longer delay to avoid race condition with activeJob useEffect
      searchTimeoutRef.current = setTimeout(() => {
        // Always build payload explicitly to ensure search runs even with only jobId
        const payload = buildSearchPayload();
        executeSearch(payload);
        // Mark as searched if not already marked
        if (!hasSearched) {
          setHasSearched(true);
        }
      }, 350);
    }

    // Always return cleanup function
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, pagination.currentPage]);

  // Active filters count for UI badge - only count filters from active sections
  // For slider fields (yearsExperience, salaryRange, salaryIncrease), always count them
  // when section is active, as default values are valid filters
  const activeFiltersCount = useMemo(() => {
    let count = 0;

    // Experience & Role
    if (activeSections.experienceRole) {
      // yearsExperience is a slider - always count when section is active
      // Default value [0, 5] is a valid filter
      count += 1;
      if (filters.seniority !== DEFAULT_FILTERS.seniority) count += 1;
    }

    // Location & Work
    if (activeSections.locationWork) {
      if (filters.country?.length > 0) count += 1;
      if (filters.city !== DEFAULT_FILTERS.city) count += 1;
      if (filters.workMode?.length > 0) count += 1;
      if (filters.willingToRelocate !== DEFAULT_FILTERS.willingToRelocate)
        count += 1;
    }

    // Availability
    if (activeSections.availability) {
      if (filters.immediateJoiner !== DEFAULT_FILTERS.immediateJoiner)
        count += 1;
      if (filters.noticePeriod !== DEFAULT_FILTERS.noticePeriod) count += 1;
      if (filters.shift !== DEFAULT_FILTERS.shift) count += 1;
      if (filters.shiftReadiness !== DEFAULT_FILTERS.shiftReadiness) count += 1;
      if (filters.timezoneOverlap !== DEFAULT_FILTERS.timezoneOverlap)
        count += 1;
      if (filters.travelPercentage !== DEFAULT_FILTERS.travelPercentage)
        count += 1;
    }

    // Compensation
    if (activeSections.compensation) {
      if (filters.compensationType !== DEFAULT_FILTERS.compensationType)
        count += 1;
      if (filters.currency !== DEFAULT_FILTERS.currency) count += 1;
      if (filters.frequency !== DEFAULT_FILTERS.frequency) count += 1;
      // salaryRange is a slider - always count when section is active
      // Default value [0, 100000] is a valid filter
      count += 1;
      // salaryIncrease is a slider - always count when section is active
      // Default value [0, 100] is a valid filter
      count += 1;
    }

    // Work Authorization
    if (activeSections.workAuth) {
      if (filters.visaWorkPermit?.length > 0) count += 1;
    }

    // Languages
    if (activeSections.languages) {
      if (filters.languages?.length > 0) count += filters.languages.length;
    }

    // Stability & Growth
    if (activeSections.stability) {
      if (filters.employmentStability !== DEFAULT_FILTERS.employmentStability)
        count += 1;
      if (filters.engagementPreference !== DEFAULT_FILTERS.engagementPreference)
        count += 1;
      if (filters.willingnessToUpskill !== DEFAULT_FILTERS.willingnessToUpskill)
        count += 1;
    }

    // Jobs Applied (section-gated like other sections)
    if (activeSections.jobsApplied) {
      if (
        filters.jobAppliedFilter !== null &&
        filters.jobAppliedFilter !== undefined
      ) {
        count += 1;
      }
    }

    return count;
  }, [filters, activeSections]);

  // ==================== SAVE/LOAD FUNCTIONALITY ====================

  /**
   * Build API payload from a search state object
   * This is used to avoid race conditions when loading saved searches
   */
  const buildPayloadFromSearchState = useCallback(
    (searchState) => {
      const payload = {
        pageNumber: pagination.currentPage,
        pageSize: pagination.pageSize,
        orderBy: searchState.sortBy || sortBy,
      };

      // Add job ID if active
      if (activeJob?.id) {
        payload.jobId = activeJob.id;
      }

      // Extract skill IDs and certification names from chips
      const skillIds = [];
      const certificationNames = [];
      const keywords = [];

      if (searchState.searchChips) {
        searchState.searchChips.forEach((chip) => {
          if (chip.type === 'skill' && chip.id) {
            skillIds.push(chip.id);
          } else if (chip.type === 'certification' && chip.value) {
            certificationNames.push(chip.value);
          } else if (chip.type === 'keyword' && chip.value) {
            keywords.push(chip.value);
          } else if (typeof chip === 'string') {
            keywords.push(chip);
          } else if (chip.value) {
            keywords.push(chip.value);
          }
        });
      }

      if (skillIds.length > 0) payload.skillIds = skillIds;
      if (certificationNames.length > 0)
        payload.certificationNames = certificationNames;
      if (keywords.length > 0) payload.keywords = keywords;

      // Add filter values only for active sections with non-default values
      const stateFilters = searchState.filters || {};
      const stateActiveSections = searchState.activeSections || {};

      Object.entries(stateActiveSections).forEach(([section, isActive]) => {
        if (!isActive) return;

        switch (section) {
          case 'experienceRole': {
            const expRange =
              stateFilters.yearsExperience || DEFAULT_FILTERS.yearsExperience;
            const [minExp, maxExp] = expRange;
            payload.minYearsExperience = minExp;
            payload.maxYearsExperience = maxExp;

            if (stateFilters.seniority)
              payload.seniority = stateFilters.seniority;
            break;
          }

          case 'locationWork':
            if (stateFilters.country?.length > 0) {
              payload.countryIds = stateFilters.country;
            }
            if (stateFilters.city) payload.city = stateFilters.city;
            if (stateFilters.workMode?.length > 0) {
              payload.workModes = stateFilters.workMode;
            }
            if (stateFilters.willingToRelocate) {
              payload.willingToRelocate = true;
            }
            break;

          case 'availability':
            if (stateFilters.immediateJoiner) payload.immediateJoiner = true;
            if (stateFilters.noticePeriod)
              payload.noticePeriod = stateFilters.noticePeriod;
            if (stateFilters.shiftReadiness) {
              payload.shiftReadiness = stateFilters.shiftReadiness;
            }
            if (stateFilters.timezoneOverlap) {
              payload.timezoneOverlap = stateFilters.timezoneOverlap;
            }
            if (stateFilters.travelPercentage !== null) {
              payload.travelPercentage = stateFilters.travelPercentage;
            }
            break;

          case 'compensation':
            if (
              stateFilters.salaryRange &&
              (stateFilters.salaryRange[0] !== DEFAULT_FILTERS.salaryRange[0] ||
                stateFilters.salaryRange[1] !== DEFAULT_FILTERS.salaryRange[1])
            ) {
              const [minSalary, maxSalary] = stateFilters.salaryRange;
              if (stateFilters.compensationType === 'Expected') {
                payload.minExpectedSalary = minSalary;
                payload.maxExpectedSalary = maxSalary;
              } else {
                payload.minCurrentSalary = minSalary;
                payload.maxCurrentSalary = maxSalary;
              }
            }
            if (stateFilters.currency) payload.currency = stateFilters.currency;
            if (stateFilters.frequency)
              payload.frequency = stateFilters.frequency;
            if (
              stateFilters.salaryIncrease &&
              (stateFilters.salaryIncrease[0] !==
                DEFAULT_FILTERS.salaryIncrease[0] ||
                stateFilters.salaryIncrease[1] !==
                  DEFAULT_FILTERS.salaryIncrease[1])
            ) {
              const [minIncrease, maxIncrease] = stateFilters.salaryIncrease;
              payload.minSalaryIncreasePercentage = minIncrease;
              payload.maxSalaryIncreasePercentage = maxIncrease;
            }
            break;

          case 'workAuth':
            if (stateFilters.visaWorkPermit?.length > 0) {
              payload.visaWorkPermitIds = stateFilters.visaWorkPermit;
            }
            break;

          case 'languages':
            if (stateFilters.languages?.length > 0) {
              const languageSelections = stateFilters.languages
                .filter((lang) => lang.language) // Only language is required
                .map((lang) => ({
                  languageId: lang.language.value,
                  ...(lang.level && { languageLevelId: lang.level.value }), // Include level only if selected
                }));

              if (languageSelections.length > 0) {
                payload.languageSelections = languageSelections;
              }
            }
            break;

          case 'stability':
            // Always send boolean values when stability section is active
            payload.employmentStability =
              stateFilters.employmentStability ??
              DEFAULT_FILTERS.employmentStability;

            // Only send willingness to upskill if user picked Yes/No (null = Any → omit)
            if (
              stateFilters.willingnessToUpskill !== null &&
              stateFilters.willingnessToUpskill !== undefined
            ) {
              payload.willingnessToUpskill = stateFilters.willingnessToUpskill;
            }

            // Only send engagement preference if selected
            if (stateFilters.engagementPreference) {
              payload.engagementPreference = stateFilters.engagementPreference;
            }
            break;

          default:
            break;
        }
      });

      // Jobs Applied filter (section-gated like other sections)
      if (
        stateActiveSections.jobsApplied &&
        stateFilters.jobAppliedFilter !== null &&
        stateFilters.jobAppliedFilter !== undefined
      ) {
        payload.jobAppliedFilter = stateFilters.jobAppliedFilter.value;
      }

      return payload;
    },
    [pagination, sortBy, activeJob]
  );

  /**
   * Get current search state as a payload for saving
   */
  const getCurrentSearchState = useCallback(() => {
    return {
      searchChips,
      filters,
      activeSections,
      sortBy,
    };
  }, [searchChips, filters, activeSections, sortBy]);

  /**
   * Load a saved search and optionally execute it
   */
  const loadSearch = useCallback(
    (searchPayload, autoExecute = true, savedSearchInfo = null) => {
      if (!searchPayload) return;

      // Set loading flag to prevent auto-check useEffect from overriding restored sections
      isLoadingSearchRef.current = true;

      // Store the original state and active search info
      if (savedSearchInfo) {
        setActiveSavedSearch(savedSearchInfo);
        setOriginalSearchState(JSON.parse(JSON.stringify(searchPayload)));
      } else {
        // Clear active saved search when loading from recent (not a saved search)
        setActiveSavedSearch(null);
        setOriginalSearchState(null);
      }

      // Restore search chips
      if (searchPayload.searchChips) {
        setSearchChips(searchPayload.searchChips);
      }

      // Restore filters
      if (searchPayload.filters) {
        setFilters(searchPayload.filters);
      }

      // Restore active sections (this is the key part for preserving user's section selections)
      if (searchPayload.activeSections) {
        setActiveSections(searchPayload.activeSections);
      }

      // Restore sort order
      if (searchPayload.sortBy) {
        setSortBy(searchPayload.sortBy);
      }

      // Clear loading flag after state updates are done
      setTimeout(() => {
        isLoadingSearchRef.current = false;
      }, 100);

      // Execute search if requested
      if (autoExecute) {
        // Set flag to add this search to recent history and store the payload
        shouldAddToRecentRef.current = true;
        searchPayloadForRecentRef.current = JSON.parse(
          JSON.stringify(searchPayload)
        ); // Deep copy

        // Build the API payload directly from the loaded searchPayload
        // to avoid race condition with state updates
        const apiPayload = buildPayloadFromSearchState(searchPayload);

        // Execute search with the built payload
        // Use setTimeout to allow UI to update first
        setTimeout(() => {
          executeSearch(apiPayload);
          setHasSearched(true);
        }, 150);
      }
    },
    [executeSearch, buildPayloadFromSearchState]
  );

  /**
   * Open save search modal
   */
  const openSaveSearchModal = useCallback(() => {
    setShowSaveSearchModal(true);
  }, []);

  /**
   * Close save search modal
   */
  const closeSaveSearchModal = useCallback(() => {
    setShowSaveSearchModal(false);
  }, []);

  /**
   * Revert to original saved search state
   */
  const revertToSaved = useCallback(() => {
    if (originalSearchState) {
      loadSearch(originalSearchState, false);
    }
  }, [originalSearchState, loadSearch]);

  /**
   * Clear active saved search and reset to empty state
   */
  const clearActiveSavedSearch = useCallback(() => {
    // Clear the active search tracking
    setActiveSavedSearch(null);
    setOriginalSearchState(null);

    // Clear all search criteria
    setSearchChips([]);
    resetFilters();
    setActiveSections({
      experienceRole: false,
      locationWork: false,
      availability: false,
      compensation: false,
      workAuth: false,
      languages: false,
      stability: false,
      jobsApplied: false,
    });

    // Clear results and reset to empty state
    setResults([]);
    setPagination({
      currentPage: 1,
      totalResults: 0,
      pageSize: 20,
      totalPages: 1,
    });
    setHasSearched(false);
  }, [resetFilters]);

  /**
   * Clear active job from URL parameters
   */
  const clearActiveJob = useCallback(() => {
    setActiveJob(null);
  }, []);

  return {
    // UI State
    isLeftPanelCollapsed,
    toggleLeftPanel,

    // Search State
    searchInput,
    setSearchInput,
    searchChips,
    addSearchChip,
    removeSearchChip,
    clearSearchChips,
    hasSearched,

    // Filter State
    filters,
    updateFilter,
    updateFilters,
    resetFilters,
    activeFiltersCount,

    // Section State
    activeSections,
    toggleSection,
    sectionHasNonDefaultValues,

    // Results State
    results,
    isLoading,
    pagination,
    setPagination,
    sortBy,
    setSortBy,

    // Selection State
    selectedCandidates,
    toggleCandidateSelection,
    clearSelection,
    selectAll,

    // Actions
    performSearch,
    buildSearchPayload,

    // Save/Load
    getCurrentSearchState,
    loadSearch,
    showSaveSearchModal,
    openSaveSearchModal,
    closeSaveSearchModal,
    activeSavedSearch,
    revertToSaved,
    clearActiveSavedSearch,
    activeJob,
    setActiveJob,
    clearActiveJob,
  };
}
